import React, { useState, useEffect, useRef } from "react";
import {
  formatCurrency,
  getDataWithJoin,
  parseCurrency,
} from "../../../utils/SamuiUtils";

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

const ItemWhModal = ({
  formDetailList,
  showItemModal,
  handleItemClose,
  itemDataList,
  onRowSelectItem,
}) => {
  // สถานะการค้นหา
  const [masterList, setMasterList] = useState([]);
  const [originalList, setOriginalList] = useState([]);
  const [baseList, setBaseList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredItemDataList, setFilteredItemDataList] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showInactiveItems, setShowInactiveItems] = useState(false);
  const searchInputRef = useRef(null);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const selectAllRef = useRef(null);
  const skipNextLocalFilterRef = useRef(false);

  const filterLocal = (term) => {
    const kw = (term || "").trim().toLowerCase();
    const source = masterList || [];

    if (!kw) {
      setFilteredItemDataList(source);
      return;
    }

    setFilteredItemDataList(
      source.filter((x) => {
        const code = (x.Item_Code || "").toLowerCase();
        const name = (x.Item_Name || "").toLowerCase();
        return code.includes(kw) || name.includes(kw);
      })
    );
  };

  const handleSelectAll = (isChecked) => {
    if (isChecked) {
      setSelectedItems((prev) => {
        const map = new Map(prev.map((x) => [x.Item_Id, x]));
        (filteredItemDataList || []).forEach((x) => map.set(x.Item_Id, x));
        return Array.from(map.values());
      });
    } else {
      // เอาเฉพาะที่อยู่ในหน้าปัจจุบันออก
      setSelectedItems((prev) =>
        prev.filter(
          (x) =>
            !(filteredItemDataList || []).some((f) => f.Item_Id === x.Item_Id)
        )
      );
    }
  };
  const allChecked =
    filteredItemDataList.length > 0 &&
    filteredItemDataList.every((x) =>
      selectedItems.some((s) => s.Item_Id === x.Item_Id)
    );

  const someChecked = filteredItemDataList.some((x) =>
    selectedItems.some((s) => s.Item_Id === x.Item_Id)
  );

  const handleCheckboxChange = (item) => {
    const isSelected = selectedItems.some(
      (selected) => selected.Item_Id === item.Item_Id
    );
    if (isSelected) {
      setSelectedItems((prevSelected) =>
        prevSelected.filter((selected) => selected.Item_Id !== item.Item_Id)
      );
    } else {
      setSelectedItems((prevSelected) => [...prevSelected, item]);
    }
  };

  const handleSearchDB = async (termOverride) => {
    const term = (termOverride ?? searchTerm ?? "").trim();

    try {
      setFilteredItemDataList([]); // loading

      const companyId = localStorage.getItem("company");
      const needFilterOnhand = [
        "/icebox",
        "/technical-equip-form",
        "/office-equip-form",
      ].includes(window.location.pathname);
      const safeTerm = term.replace(/'/g, "''");
      const whereSearch =
        safeTerm !== ""
          ? `AND (v.Item_Code LIKE '%${safeTerm}%' OR v.Item_Name LIKE '%${safeTerm}%')`
          : "";
      const statusFilter = showInactiveItems ? "" : "AND ISNULL(v.is_Active, v.Item_Status) = 'Y'";

      const data = await getDataWithJoin(
        `
        v.Item_Id, v.Item_Barcode, v.Item_Code, 
        v.Item_Name, v.Item_Name_EN, v.Item_Short_Name, 
        v.Item_Unit_IN, v.Item_Unit_ST, v.Item_Unit_OUT, 
        v.Item_Cost, v.Item_Price, v.Item_PriceA, v.Item_PriceB, 
        v.Item_Brand, v.Item_Series, v.Item_Colour, v.Size_Line,
        ISNULL(wh.Item_Onhand, 0) AS Item_Onhand
      `,
        `View_Set_Item v`,
        `
        LEFT JOIN (
          SELECT Item_Id, SUM(Item_Onhand) AS Item_Onhand
          FROM API_1101_WH_ITEM_ONHAND
          GROUP BY Item_Id
        ) wh ON wh.Item_Id = v.Item_Id
      `,
        `
        WHERE v.Comp_Id = ${companyId}
        ${statusFilter}
        ${whereSearch}
        ${needFilterOnhand ? "AND wh.Item_Onhand > 0" : ""}
        ORDER BY v.Item_Short_Name, v.Item_Brand, v.Item_Series, v.Item_Colour, v.Size_Line
      `
      );

      //  ถ้า term ว่าง = โหลดทั้งหมด -> อัปเดตฐานล่าสุด
      if (term === "") {
        skipNextLocalFilterRef.current = true;
        setMasterList(data || []);
        setOriginalList(data || []);
        setBaseList(data || []);
        setFilteredItemDataList(data || []);
        return;
      }

      skipNextLocalFilterRef.current = true;
      setMasterList(data || []);
      setBaseList(data || []);
      setFilteredItemDataList(data || []);
    } catch (error) {
      alert(`เกิดข้อผิดพลาด: ${error.message || error}`);
      setFilteredItemDataList([]);
    }
  };
  const handleReset = () => {
    skipNextLocalFilterRef.current = true;
    setSearchTerm("");
    handleSearchDB(""); // โหลดใหม่ทั้งหมดจาก DB
    searchInputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearchDB();
    }
  };

  useEffect(() => {
    if (!showItemModal) return;

    if (skipNextLocalFilterRef.current) {
      skipNextLocalFilterRef.current = false;
      return;
    }

    filterLocal(debouncedSearchTerm);
  }, [debouncedSearchTerm, showItemModal, masterList]);

  useEffect(() => {
    if (showItemModal) {
      setSearchTerm("");
      setOriginalList(itemDataList || []);
      setBaseList(itemDataList || []);
      setMasterList(itemDataList || []);
      setFilteredItemDataList(itemDataList || []);
      searchInputRef.current?.focus();
    }
  }, [showItemModal, itemDataList]);

  // useEffect(() => {
  //   if (showItemModal) {
  //     setSearchTerm("");
  //     setOriginalList(itemDataList || []);
  //     setBaseList(itemDataList || []);
  //     setFilteredItemDataList(itemDataList || []);
  //     searchInputRef.current?.focus();
  //   }
  // }, [showItemModal, itemDataList]);

  useEffect(() => {
    if (showItemModal) {
      setSelectedItems(
        formDetailList
          .map((item) =>
            (masterList || []).find((x) => x.Item_Id === item.itemId)
          )
          .filter(Boolean)
      );
    }
  }, [showItemModal, formDetailList, masterList]);

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = !allChecked && someChecked;
    }
  }, [allChecked, someChecked]);

  return (
    <>
      <div
        className={`modal ${showItemModal ? "show" : ""}`}
        style={{ display: showItemModal ? "block" : "none" }}
        tabIndex="-1"
        role="dialog"
      >
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">รายชื่อสินค้าในคลัง</h5>
              <button type="button" className="close" onClick={handleItemClose}>
                <span>&times;</span>
              </button>
            </div>
            <div className="modal-body">
             
              <div
                className="form-group"
                style={{ display: "flex", alignItems: "center" }}
              >
                <div style={{ position: "relative", flex: 1, marginRight: 10 }}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="ค้นหา รหัสสินค้า หรือ ชื่อสินค้า"
                    value={searchTerm}
                    onKeyDown={handleKeyDown}
                    ref={searchInputRef}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ paddingRight: 38 }}
                  />

                  {searchTerm?.trim() !== "" && (
                    <button
                      type="button"
                      onClick={handleReset}
                      aria-label="clear"
                      style={{
                        position: "absolute",
                        right: 8,
                        top: "50%",
                        transform: "translateY(-50%)",
                        border: "none",
                        background: "transparent",
                        fontSize: 18,
                        cursor: "pointer",
                        lineHeight: 1,
                        padding: 0,
                        color: "#999",
                      }}
                      onMouseDown={(e) => e.preventDefault()} // กัน input หลุด focus
                      title="ล้างคำค้นหา"
                    >
                      ×
                    </button>
                  )}
                </div>

                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ marginRight: 10, height: "35px" }}
                  onClick={() => handleSearchDB()}
                >
                  ค้นหา
                </button>
              </div>
               <div style={{ marginBottom: 10 }}>
                <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={showInactiveItems}
                    onChange={(e) => setShowInactiveItems(e.target.checked)}
                    style={{ marginRight: 8 }}
                  />
                  <span style={{width:"560px"}}>แสดงสินค้ารายการเก่า</span>
                </label>
              </div>
              <div
                className="table-responsive"
                style={{ maxHeight: "600px", overflowY: "auto" }}
              >
                <table className="table table-striped table-hover">
                  <thead className="thead-dark">
                    <tr>
                      <th className="text-center" style={{ maxWidth: "10px" }}>
                        <input
                          style={{ maxWidth: "20px" }}
                          type="checkbox"
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          checked={allChecked}
                          ref={selectAllRef}
                        />
                      </th>
                      <th className="text-center">รหัสสินค้า</th>
                      <th className="text-center" style={{ width: "800px" }}>
                        ชื่อสินค้า
                      </th>
                      <th className="text-center" style={{ width: "150px" }}>
                        สินค้าคงเหลือ
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItemDataList && filteredItemDataList.length > 0 ? (
                      filteredItemDataList.map((item, index) => (
                        <tr
                          key={item.Item_Id || index + 1}
                          onClick={() => handleCheckboxChange(item)}
                          style={{
                            cursor: "pointer",
                            backgroundColor: selectedItems.some(
                              (selectedItem) =>
                                selectedItem.Item_Id === item.Item_Id
                            )
                              ? "rgb(105, 188, 216)"
                              : "transparent",
                            transition: "background-color 0.3s ease",
                          }}
                        >
                          <td
                            className="text-center"
                            style={{ maxWidth: "100px" }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedItems.some(
                                (selected) => selected.Item_Id === item.Item_Id
                              )}
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => handleCheckboxChange(item)}
                              style={{ maxWidth: "20px", cursor: "pointer" }}
                            />
                          </td>
                          <td className="text-center">{item.Item_Code}</td>
                          <td className="text-left">{item.Item_Name}</td>
                          <td className="text-left">
                            {formatCurrency(item.Item_Onhand)}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4">
                          <center>
                            <h5>ไม่พบข้อมูล</h5>
                          </center>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-primary"
                onClick={() => onRowSelectItem(selectedItems)}
                //disabled={selectedItems.length === 0} // คอมเม้นไว้ก่อน ใช้สำหรับ Disabled เมื่อยังไม่ได้เลือกสินค้า
              >
                ยืนยัน
              </button>
              <button className="btn btn-danger" onClick={handleItemClose}>
                ปิด
              </button>
            </div>
          </div>
        </div>
      </div>
      {showItemModal && <div className="modal-backdrop fade show"></div>}
    </>
  );
};

export default ItemWhModal;
